import type {
  FieldType,
  FieldValue,
  RelationshipSummaryValue,
  RelationshipValue,
  TeamSelectorValue,
} from '@kizen/api';
import type { Country } from '@kizen/api/geography';
import type { CustomObjectField } from '@kizen/api/custom-object';
import type { TimezoneName } from 'countries-and-timezones';
import { markdownToHtmlSync } from '@kizen/parse-md';
import { format } from 'date-fns/format';
import { formatInTimeZone } from 'date-fns-tz';
import get from 'lodash/get';
import parsePhoneNumber from 'libphonenumber-js/max';
import { isTimezoneEqualToLocal } from 'app/timezone';
import { BusinessVariable } from '__components/WYSIWYG/use-merge-fields';

export const isMergeField = {
  automationVariable: (mf: string) => mf.startsWith('automation_variable.'),
  businessVariable: (mf: string) => mf.startsWith('business_variable.'),
  contact: (mf: string) => mf.startsWith('contact.'),
  /**
   * "entity_record" is used in automations to refer to the automation's object when it is a non-contact automation
   */
  entityRecord: (mf: string) => mf.startsWith('entity_record.'),
  /**
   * Not a real field, but a special merge field that will produce a URL to the entity details page
   */
  linkUrlField: (mf: string) => mf.endsWith('.link_url'),
  staticBusinessVariable: (mf: string) => mf.startsWith('business.'),
  teamMember: (mf: string) => mf.startsWith('team_member.'),
};

export const mergeFieldRegex = {
  /**
   * Parse merge field tokens e.g. `contact.first_name` out of WYSIWYG html.
   *
   * Sample HTML:
   * ```html
   *   <p data-line-height="default" style="line-height: 1.25; text-align: left">
   *     Agent/Agency:
   *     <span
   *       class="kzn-merge-field"
   *       data-merge-field-fallback-label="Primary Company Record"
   *       data-merge-field-relationship="contact.company_primary"
   *       >{{ contact.company_primary }}</span
   *     >
   *   </p>
   *   <p data-line-height="default" style="line-height: 1.25; text-align: left">
   *     <span
   *       class="kzn-merge-field"
   *       data-merge-field-fallback-label="Rich Text"
   *       data-merge-field-relationship="contact.rich_text"
   *       data-merge-field-unescaped="true"
   *       >{{{ contact.rich_text }}}</span
   *     >
   *   </p>
   * ```
   */
  html: () =>
    /data-merge-field-relationship="([^"]+)"[^>]*>{{2,3}\s*\1\s*}{2,3}/g,
  /**
   * The first capture group returns `{{` or `{{{`. Three curly braces signifies unescaped content.
   * The second capture group returns the object/field data e.g. `contact.first_name`.
   *
   * Sample Text: `start {{ contact.first_name }} middle ... {{{ contact.rich_text }}} end`
   */
  text: () => /(\{{2,3})\s*([0-9A-Za-z._-]+)\s*(\}{2,3})/g,
  /**
   * Finds all unescaped merge field tokens i.e. merge fields wrapped in 3 curly braces.
   */
  unescaped: () => /{{{\s*([0-9A-Za-z._-]+)\s*}}}/g,
};

/**
 * Flattens arrays types out of a union yielding their type along
 * with all other non-array types. Recursively flattens array types
 * and dedupes the final result.
 *
 * Useful when a type looks like `T | T[]` and you want `T`.
 *
 * @example
 * ```ts
 * // T | T[] --> T
 * type Union = { name: string } | { name: string }[];
 * type Flattened = FlattenUnion<Union>; // { name: string }
 * ```
 * @example
 * ```ts
 * // A | A[] | B[] --> A | B
 * type Union =
 *  | { name: string }
 *  | { name: string }[]
 *  | { id: string }[];
 * type Flattened = FlattenUnion<Union>; // { name: string } | { id: string }
 * ```
 * @example
 * ```ts
 * // A | B[] | C[][] --> A | B | C
 *  * type Union =
 *  | { a: string }
 *  | { b: string }[]
 *  | { c: string }[][];
 * type Flattened = FlattenUnion<Union>; // { a: string } | { b: string } | { c: string }
 * ```
 */
type FlattenUnion<T, Original = T> = T extends any[]
  ? MaybeAdd<WithoutArrays<Original>, FlattenUnion<T[number]>>
  : T;
type WithoutArrays<T> = T extends any[] ? never : T;
type MaybeAdd<A, B> = B extends A ? A : A | B;

export type FieldValueWithRelations =
  | Exclude<FieldValue, RelationshipValue>
  | RelationshipValueWithRelation;

export type RelationshipValueWithRelation = RelationshipValue & {
  relation: CustomObjectField['relation'];
};

type BusinessLike = {
  city: string;
  country: string;
  name: string;
  phone: string;
  phone_default_region: string;
  postal_code: string;
  street_address: string;
  reply_to_email: string;
  state: string;
  timezone: { name: TimezoneName };
  primary_marketing_contact_name: string;
  primary_marketing_contact_email: string;
};

type PhoneRegion = {
  value: string;
  label: string;
};

type GetMergeFieldLookupOptions = {
  business: BusinessLike;
  countries?: Country[];
  phoneRegions?: PhoneRegion[];
};

type GetTeamMemberMergeFieldLookup = {
  teamMember: {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
  };
  roles?: string[];
  phoneRegions?: PhoneRegion[];
  business: BusinessLike;
};

type FieldLike = {
  id: string;
  meta: Record<string, any>;
  name: string;
};

const getRelationshipElement = (
  value: FlattenUnion<
    Exclude<RelationshipValue, RelationshipSummaryValue>['value']
  >,
  relation: CustomObjectField['relation']
) => {
  const link = getRelationLink(value.id, relation);

  if (isTeamSelectorValue(value)) {
    let display = `${value.first_name} ${value.last_name}`.trim();

    if (value.email) {
      display = display ? `${display} (${value.email})` : value.email;
    }

    return `<a target="_blank" href="${link}">${display}</a>`;
  }

  return `<a target="_blank" href="${link}">${value.display_name}</a>`;
};

const isTeamSelectorValue = (
  value: any
): value is TeamSelectorValue['value'] => {
  if (value && typeof value === 'object') {
    return (
      typeof value.first_name === 'string' &&
      typeof value.last_name === 'string' &&
      typeof value.email === 'string'
    );
  }
  return false;
};

const getCodeFromRegion = (phoneRegion?: PhoneRegion): string => {
  if (!phoneRegion) return '';
  const matches = phoneRegion.label.match(/.*\((.*)\)/); // ex. United States (+1)
  return matches?.pop() ?? '';
};

const getRelationLink = (
  fieldId: string,
  relation: CustomObjectField['relation']
) => {
  if (relation.fetch_url === 'client') {
    return `/client/${fieldId}/details`;
  }

  return `/custom-objects/${relation.related_object}/${fieldId}/details`;
};

const getMergeFieldValue = (
  fv: FieldValueWithRelations,
  options: { t: (x: string) => string; field?: FieldLike }
): string => {
  const { t, field } = options;

  if ('value_summary' in fv) {
    return `${fv.value_summary.count} ${t('records')}`;
  }

  if (fv.value === undefined || fv.value === null) {
    return '';
  }

  switch (fv.field_type) {
    case 'checkbox':
      return fv.value ? t('Is Checked') : t('Is Not Checked');
    case 'checkboxes': {
      const checked = fv.value.map((option) => option.name);
      return checked.join(', ');
    }
    case 'date':
      if (!fv.value) {
        return '';
      }
      return format(fv.value, 'MMMM dd, yyyy');
    case 'datetime':
      if (!fv.value) {
        return '';
      }
      return format(fv.value, 'MMMM dd, yyyy h:mm a');
    case 'decimal':
      return fv.value.toString();
    case 'dropdown':
      return fv.value.name;
    case 'dynamictags':
      return fv.value.map((option) => option.name).join(', ');
    case 'files':
      return fv.value.map((file) => file.name).join(', ');
    case 'integer':
      return fv.value.toString();
    case 'money':
      if (!fv.value) {
        return '';
      }
      return `${fv.value.symbol}${fv.value.amount}`;
    case 'phonenumber':
      try {
        const parsedPhone = parsePhoneNumber(fv.value.e164);
        if (parsedPhone) {
          return parsedPhone.formatInternational();
        }
        return fv.value.e164;
      } catch (err) {
        return '';
      }
    case 'radio':
      return fv.value.name;
    case 'rating':
      return fv.value.toString();
    case 'relationship': {
      if (Array.isArray(fv.value)) {
        return fv.value
          .map((x) => getRelationshipElement(x, fv.relation))
          .join(', ');
      }

      return getRelationshipElement(fv.value, fv.relation);
    }
    case 'status':
      return fv.value.name;
    case 'team_selector': {
      if (fv.value.first_name || fv.value.last_name) {
        const name =
          `${fv.value.first_name ?? ''} ${fv.value.last_name ?? ''}`.trim();

        if (fv.value.email) {
          return `${name} (${fv.value.email})`;
        }

        return name;
      }

      return fv.value.email ?? '';
    }
    case 'timezone':
      return fv.value.name;
    case 'yesnomaybe':
      return fv.value.name;
    case 'longtext':
      if (field?.meta.isMarkdown) {
        return markdownToHtmlSync(fv.value);
      }

      return fv.value.split('\n').join('<br />');
    case 'email':
    case 'text':
      return fv.value;
  }
};

class MergeFieldProcessor {
  private document: Document;
  private parents: Map<string, Element>;

  constructor(
    private content: string,
    private lookup: Record<string, Record<string, string>>
  ) {
    this.document = new DOMParser().parseFromString(content, 'text/html');
    this.parents = new Map();
  }

  result() {
    return this.document.body.innerHTML;
  }

  /**
   * Preprocesses the document to associate merge field nodes with a different parent element. By default,
   * merge field content will be set as the innerHTML of the merge field node. This won't work for content
   * that is HTML like rich text because the merge field node is a span and block elements within a span
   * will be reorganized by the browser when rendering. The outermost <p> element will be replaced with the
   * full merge field value when it is marked as unescaped.
   */
  collectMergeFieldParents() {
    const nodes = this.document.querySelectorAll(
      '.kzn-merge-field[data-merge-field-unescaped="true"]'
    );

    for (const node of nodes) {
      const mf = node.getAttribute('data-merge-field-relationship');

      if (!mf) {
        continue;
      }

      this.parents.set(mf, node.closest('p') ?? node);
    }

    return this;
  }

  /**
   * Replaces merge fields with two curly braces: `{{ client_client.first_name }}`
   */
  replaceMergeFields() {
    this.document.body.innerHTML = this.document.body.innerHTML.replace(
      /{{\s*([0-9A-Za-z._-]+)\s*}}/g,
      (_match, path) => {
        const value = get(this.lookup, path, '');
        return typeof value === 'string' ? value : '';
      }
    );

    return this;
  }

  /**
   * Replaces merge fields with three curly braches - `{{{ client_client.rich_text }}}
   *
   * A rich text merge field will be transformed from
   * ```html
   *  <p data-line-height="default" style="line-height: 1.25">
   *    <span style="font-family: Montserrat, sans-serif; font-size: 18px">
   *      <strong>
   *        <span
   *          class="kzn-merge-field"
   *          data-merge-field-fallback-label="Rich Text"
   *          data-merge-field-relationship="client_client.rich_text"
   *          data-merge-field-unescaped="true"
   *        >
   *          {{{ client_client.rich_text }}}
   *        </span></strong
   *      ></span
   *    >
   *  </p>
   *   ```
   * into
   * ```html
   * <div
   *   data-merge-field-relationship="client_client.rich_text"
   *   data-line-height="default"
   *   style="line-height: 1.25; font-size: 18px; font-family: Tahoma"
   * >
   *   <h1>Heading 1</h1>
   *   <pre>
   *     <code>
   *       let variable: string = 'testing';
   *       let other: number = 123
   *      </code>
   *    </pre>
   *    <p>
   *      <a href="https://google.com" target="_blank" rel="noopener noreferrer">Google</a>
   *    </p>
   *    <ol>
   *      <li><p>one</p></li>
   *      <li><p>two</p></li>
   *      <li><p>three</p></li>
   *    </ol>
   *  </div>
   * ```
   */
  replaceUnescapedMergeFields() {
    const unescaped = this.content.matchAll(mergeFieldRegex.unescaped());

    for (const [_match, path] of unescaped) {
      const value = get(this.lookup, path, '');

      if (typeof value !== 'string') {
        continue;
      }

      const parent = this.parents.get(path);

      if (parent) {
        const div = this.document.createElement('div');

        div.innerHTML = value;

        div.setAttribute('data-merge-field-relationship', path);
        div.setAttribute('data-merge-field-unescaped', 'true');

        for (const attr of parent.attributes) {
          div.setAttribute(attr.name, attr.value);
        }

        const span = parent.firstElementChild;

        if (this.isSpan(span)) {
          if (span.style.fontSize) {
            div.style.setProperty('font-size', span.style.fontSize);
          }

          if (span.style.fontFamily) {
            div.style.setProperty('font-family', span.style.fontFamily);
          }
        }

        for (const anchor of div.querySelectorAll('a')) {
          anchor.setAttribute('target', '_blank');
          anchor.setAttribute('rel', 'noopener noreferrer');
        }

        parent.replaceWith(div);
      }
    }

    return this;
  }

  private isSpan(element: Element | null): element is HTMLSpanElement {
    return element?.tagName === 'SPAN';
  }
}

const defMergeFieldProcessor = (
  content: string,
  lookup: Record<string, Record<string, string>>
): MergeFieldProcessor => {
  return new MergeFieldProcessor(content, lookup);
};

/**
 * Business merge fields look like `{{ business.name }}`. This utility should be used to create
 * an object such that the `business.name` path lookup resolves when using lodash/get.
 *
 * ```ts
 * const lookup = {
 *  business: getBusinessMergeFieldLookup(...)
 * }
 * ```
 *
 * @param param0
 * @returns
 */
export const getBusinessMergeFieldLookup = ({
  business,
  countries,
  phoneRegions,
}: GetMergeFieldLookupOptions) => {
  const now = new Date();
  const businessTimezone = business.timezone.name;
  const businessDate = now.toLocaleString('en-US', {
    timeZone: businessTimezone,
  });
  const defaultCallingCode = getCodeFromRegion(
    phoneRegions?.find((x) => x.value === business.phone_default_region)
  );
  let businessTime = format(businessDate, 'h:mm a');
  if (!isTimezoneEqualToLocal(businessTimezone)) {
    const offset = formatInTimeZone(now, businessTimezone, 'XXX');
    businessTime += ` ${businessTimezone} (GMT${offset})`;
  }

  let businessPhone = business.phone;
  if (businessPhone) {
    const parsedPhone = parsePhoneNumber(business.phone);
    if (parsedPhone) {
      businessPhone =
        defaultCallingCode && business.phone.startsWith(defaultCallingCode)
          ? parsedPhone.formatNational()
          : parsedPhone.formatInternational();
    }
  }

  const country = countries?.find((c) => c.id === business.country);
  let state;
  if (country) {
    state = country.subdivision_groups
      .flatMap((x) => x.subdivisions)
      .find((y) => y.id === `${country.id}-${business.state}`);
  }

  return {
    city: business.city,
    reply_to_email: `<a href="mailto:${business.reply_to_email}">${business.reply_to_email}</a>`,
    country: country?.name ?? business.country,
    country_code: country?.id ?? business.country,
    name: business.name,
    phone: businessPhone,
    postal_code: business.postal_code,
    street_address: business.street_address,
    state: state?.name ?? business.state,
    state_code: business?.state,
    time: businessTime,
    primary_marketing_contact_name: business.primary_marketing_contact_name,
    primary_marketing_contact_email: business.primary_marketing_contact_email,
  };
};

/**
 * Custom Business Variables merge fields look like `{{ business_variable.api_name }}`. This utility should be used to create
 * an object such that the `business_variable.api_name` path lookup resolves when using lodash/get.
 *
 * ```ts
 * const lookup = {
 *  business_variable: getBusinessVariablesMergeFieldLookup(
 *   chosenBusiness.business_variables
 *  )
 * }
 * ```
 *
 * @param param0
 * @returns
 */
export const getBusinessVariablesMergeFieldLookup = (
  businessVariables: BusinessVariable[] = []
) => {
  return businessVariables.reduce<Record<string, string>>(
    (acc, { api_name, value_string }) => {
      if (api_name) {
        acc[api_name] = value_string;
      }
      return acc;
    },
    {}
  );
};

/**
 * Team member merge fields look like `{{ team_member.email }}`. This utility should be used to create
 * an object such that the `team_member.email` path lookup resolves when using lodash/get.
 *
 * ```ts
 * const lookup = {
 *  team_member: getTeamMemberMergeFieldLookup(...)
 * }
 * ```
 *
 * @param param0
 * @returns
 */
export const getTeamMemberMergeFieldLookup = ({
  teamMember,
  roles,
  phoneRegions,
  business,
}: GetTeamMemberMergeFieldLookup) => {
  const now = new Date();
  let teamMemberPhone = teamMember?.phone;
  const defaultCallingCode = getCodeFromRegion(
    phoneRegions?.find((x) => x.value === business.phone_default_region)
  );
  if (teamMemberPhone) {
    const parsedPhone = parsePhoneNumber(teamMemberPhone);
    if (parsedPhone) {
      teamMemberPhone =
        defaultCallingCode && teamMember.phone.startsWith(defaultCallingCode)
          ? parsedPhone.formatNational()
          : parsedPhone.formatInternational();
    }
  }

  return {
    email: `<a href="mailto:${teamMember.email}">${teamMember.email}</a>`,
    first_name: teamMember.first_name,
    last_name: teamMember.last_name,
    roles: roles?.join(', ') ?? '',
    phone: teamMemberPhone,
    date: format(now, 'MMMM dd, yyyy'),
    time: format(now, 'h:mm a'),
  };
};

export const getMergeFieldLookupForFields = (
  values: FieldValueWithRelations[],
  fields: Map<string, FieldLike>,
  t: (x: string) => string
): Record<string, string> => {
  const lookup = values.reduce<Record<string, string>>((acc, fv) => {
    const field = fields.get(fv.id);
    acc[fv.name as string] = getMergeFieldValue(fv, { t, field });
    return acc;
  }, {});

  return lookup;
};

/**
 * Example merge fields
 * - `{{ business.name }}`
 * - `{{ team_member.first_name }}`
 * - `{{ my_object_dk32eds.text_dk3kf }}`
 * - `{{{ client_client.rich_text }}}
 */
export const replaceMergeFields = (
  content: string,
  lookup: Record<string, Record<string, string>>
) => {
  const processor = defMergeFieldProcessor(content, lookup);

  const result = processor
    .collectMergeFieldParents()
    .replaceUnescapedMergeFields()
    .replaceMergeFields()
    .result();

  return result;
};

/**
 * @example
 * ```ts
 * const content = '{{ team_member.first_name }} is a part of {{ business.name }} business.':
 * const mergeFields = parseMergeFields(content);
 * // ['team_member.first_name', 'business.name'];
 * ```
 */
export const parseMergeFields = (content: string): string[] => {
  const set = new Set<string>();

  const matches = content.matchAll(mergeFieldRegex.text());

  for (const match of matches) {
    set.add(match[2]);
  }

  return Array.from(set);
};

type FieldTypeProp = { field_type: FieldType };
type FieldTypePropCamel = SnakeToCamelCase<FieldTypeProp>;

export function isMergeFieldUnescaped(field: FieldTypeProp): boolean;
export function isMergeFieldUnescaped(field: FieldTypePropCamel): boolean;
export function isMergeFieldUnescaped(
  field: FieldTypeProp | FieldTypePropCamel
) {
  const type = 'field_type' in field ? field.field_type : field.fieldType;

  return type === 'files' || type === 'longtext';
}
